import { Component } from '@theme/component';
import { debounce, isLaptopBreakpoint, isTabletBreakpoint, isMobileBreakpoint } from '@theme/utilities';

export class Masonry extends Component {
  static #STYLE_VARS = [
    'columns', 'columns-laptop', 'columns-tablet', 'columns-mobile',
    'columns-xs', 'columns-sm', 'columns-xl', 'columns-xxl',
    'horizontal-gap', 'vertical-gap', 'horizontal-gap-mobile', 'vertical-gap-mobile',
    'height-limit', 'height-limit-mobile'
  ];

  #handleResize = debounce(() => this.#layout(), 100);
  #styleCache = null;
  #isExpanded = false;
  #showMoreScrollY = null;

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => this.#layout());

    window.addEventListener('resize', this.#handleResize);

  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#handleResize);
    this.#handleResize.cancel?.();
    super.disconnectedCallback();
    this.#styleCache = null;
  }

  toggleShowMore() {
    const btn = this.querySelector('.masonry-block__show-more-button');
    const itemsContainer = this.querySelector('.masonry-block__items');
    if (!itemsContainer) return;

    const limitHeight = isMobileBreakpoint()
      ? parseFloat(this.dataset.heightMobile || this.dataset.height) || 0
      : parseFloat(this.dataset.height) || 0;

    if (this.#isExpanded) {
      // Collapse: animate max-height from fullHeight → limitHeight
      this.#isExpanded = false;
      const fullHeight = parseFloat(itemsContainer.style.height) || 0;

      if (this.dataset.height) this.style.setProperty('--height-limit', this.dataset.height);
      if (this.dataset.heightMobile) this.style.setProperty('--height-limit-mobile', this.dataset.heightMobile);
      this.classList.add('has-overlay-gradient');
      this.#styleCache = null;
      if (btn) btn.textContent = btn.dataset.showMore;

      // Pin start value then trigger CSS transition
      itemsContainer.style.maxHeight = `${fullHeight}px`;
      itemsContainer.offsetHeight; // force reflow so browser records start value
      itemsContainer.style.maxHeight = `${limitHeight}px`;

      itemsContainer.addEventListener('transitionend', () => {
        itemsContainer.style.maxHeight = ''; // yield to CSS var (already restored above)
      }, { once: true });

      // Scroll back simultaneously with collapse animation
      if (this.#showMoreScrollY !== null) {
        this.#animateScrollTo(this.#showMoreScrollY, 300);
        this.#showMoreScrollY = null;
      }
    } else {
      // Save scroll position before expanding
      this.#showMoreScrollY = window.scrollY;

      // Expand: animate max-height from limitHeight → fullHeight
      this.style.removeProperty('--height-limit');
      this.style.removeProperty('--height-limit-mobile');
      this.#styleCache = null;
      this.querySelectorAll('.masonry-item-block').forEach((item) => { item.inert = false; });
      if (btn) btn.textContent = btn.dataset.showLess;
      this.#isExpanded = true;

      // Pin start at limit, compute full height, then trigger CSS transition
      itemsContainer.style.maxHeight = `${limitHeight}px`;
      this.#layout(); // sets itemsContainer.style.height = fullHeight
      const fullHeight = parseFloat(itemsContainer.style.height) || 0;
      itemsContainer.offsetHeight; // force reflow so browser records start value
      itemsContainer.style.maxHeight = `${fullHeight}px`;

      itemsContainer.addEventListener('transitionend', () => {
        itemsContainer.style.maxHeight = ''; // yield to CSS var (auto, since removed above)
        this.classList.remove('has-overlay-gradient');
      }, { once: true });
    }
  }

  #animateScrollTo(targetY, duration) {
    const startY = window.scrollY;
    const delta = targetY - startY;
    if (delta === 0) return;
    const startTime = performance.now();
    const easeInOut = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      window.scrollTo(0, startY + delta * easeInOut(progress));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  #getStyleVars() {
    if (this.#styleCache) return this.#styleCache;
    const style = getComputedStyle(this);
    this.#styleCache = Object.fromEntries(
      Masonry.#STYLE_VARS.map((name) => [name, parseFloat(style.getPropertyValue(`--${name}`)?.trim()) || 0])
    );
    return this.#styleCache;
  }

  #getConfig() {
    const styleVars = this.#getStyleVars();
    const width = window.innerWidth;
    let columns, columnGap, rowGap, heightLimit;
    if (isMobileBreakpoint()) {
      // <768: xs (<480) and sm (480-767), both fall back to columns-mobile
      const mobileColumns = width < 480 ? styleVars['columns-xs'] : styleVars['columns-sm'];
      columns = mobileColumns || styleVars['columns-mobile'] || 2;
      columnGap = styleVars['horizontal-gap-mobile'] || 0;
      rowGap = styleVars['vertical-gap-mobile'] || 0;
      heightLimit = styleVars['height-limit-mobile'] || null;
    } else if (isTabletBreakpoint()) {
      // 768-1024 (md)
      columns = styleVars['columns-tablet'] || styleVars['columns'] || 2;
      columnGap = styleVars['horizontal-gap'] || 0;
      rowGap = styleVars['vertical-gap'] || 0;
      heightLimit = styleVars['height-limit'] || null;
    } else if (isLaptopBreakpoint()) {
      // 1025-1279 (lg)
      columns = styleVars['columns-laptop'] || styleVars['columns'] || 3;
      columnGap = styleVars['horizontal-gap'] || 0;
      rowGap = styleVars['vertical-gap'] || 0;
      heightLimit = styleVars['height-limit'] || null;
    } else {
      // >=1280: xl (1280-1440) and xxl (>=1441), both fall back to columns
      const desktopColumns = width >= 1441 ? styleVars['columns-xxl'] : styleVars['columns-xl'];
      columns = desktopColumns || styleVars['columns'] || 3;
      columnGap = styleVars['horizontal-gap'] || 0;
      rowGap = styleVars['vertical-gap'] || 0;
      heightLimit = styleVars['height-limit'] || null;
    }
    // Original height limit from data attributes — unaffected by expand/collapse state
    const originalHeightLimit = parseFloat(
      isMobileBreakpoint() ? (this.dataset.heightMobile || this.dataset.height) : this.dataset.height
    ) || null;
    return { columns: Math.max(1, columns), columnGap, rowGap, heightLimit, originalHeightLimit };
  }

  #layout() {
    const items = [...this.querySelectorAll('.masonry-item-block')];
    if (!items.length) return;

    // Phase 1: read all layout values before any writes
    let containerWidth = this.offsetWidth;
    if (!containerWidth) return;
    if (this.classList.contains('masonry-block--boxed')) {
      containerWidth = this.querySelector('.masonry-block__items').offsetWidth;
    }

    const { columns, columnGap, rowGap, heightLimit, originalHeightLimit } = this.#getConfig();
    const itemWidth = (containerWidth - (columns - 1) * columnGap) / columns;

    this.classList.add('is-masonry');

    // Phase 2: write widths, then flush with a single reflow
    items.forEach((item) => { item.style.width = `${itemWidth}px`; });
    // eslint-disable-next-line no-unused-expressions
    this.offsetHeight; // intentional flush so offsetHeight reads below are accurate

    // Phase 3: read all item heights in one batch (no writes between reads)
    const heights = items.map((item) => item.offsetHeight);

    // Phase 4: compute positions using pre-read heights, write all transforms in one batch
    const colHeights = new Array(columns).fill(0);
    items.forEach((item, i) => {
      const shortestColIndex = colHeights.indexOf(Math.min(...colHeights));
      const left = shortestColIndex * (itemWidth + columnGap);
      const top = colHeights[shortestColIndex];
      item.style.transform = `translate(${left}px, ${top}px)`;
      colHeights[shortestColIndex] = top + heights[i] + rowGap;
      item.inert = !!(heightLimit && !this.#isExpanded && top + heights[i] / 2 > heightLimit);
    });

    const heightBlock = Math.max(...colHeights) - rowGap;
    if (originalHeightLimit && heightBlock > originalHeightLimit) {
      this.classList.add('has-limit-height');
      if (!this.#isExpanded) this.classList.add('has-overlay-gradient');
      else this.classList.remove('has-overlay-gradient');
    } else {
      this.classList.remove('has-limit-height', 'has-overlay-gradient');
    }

    const itemsContainer = this.querySelector('.masonry-block__items');
    itemsContainer.style.height = `${heightBlock}px`;
  }
}

if (!customElements.get('masonry-component')) {
  customElements.define('masonry-component', Masonry);
}
